/** Contenido de la guía rápida de la app (ver QuickGuide.tsx). */

export interface GuideSection {
  id: string;
  title: string;
  intro?: string;
  steps: string[];
  note?: string;
}

export const MONITOR_GUIDE_SECTIONS: GuideSection[] = [
  {
    id: 'plan-semana',
    title: 'Cambiar la rutina del gimnasio de un día',
    intro: 'Es lo que ven todos los socios en Rutinas → Hoy.',
    steps: [
      'En esta pantalla, pulsa "Editar" en el día que quieras cambiar.',
      'Añade ejercicios con "+ Añadir" (busca por nombre en el catálogo) o quita los que sobren. Mínimo 3, máximo 20.',
      'En cada ejercicio ajusta series, repeticiones, descanso y notas. Puedes reordenarlos.',
      'Guarda. El cambio vale para ese día de la semana de aquí en adelante; los entrenos ya hechos no se tocan.',
    ],
    note:
      'Solo se guarda la versión intermedia. Principiante hace una serie menos, descansa más y se salta el último ejercicio; avanzado hace una serie más y descansa menos. Por eso conviene que el último ejercicio sea un accesorio.',
  },
  {
    id: 'rutina-propia',
    title: 'Crear una rutina con los ejercicios que tú elijas',
    steps: [
      'Ve a Rutinas → Mis rutinas y pulsa "+ Crear rutina".',
      'Ponle nombre y añade ejercicios uno a uno con "+ Añadir" (se buscan por nombre en el catálogo).',
      'Configura cada ejercicio: tipo (series y repeticiones, tiempo, distancia…), series, repeticiones, descanso y notas.',
      'Guarda. La rutina queda en tu lista y puedes colocarla en un día de tu semana con el selector de día.',
      'También puedes pulsar "Importar" y subir un Excel (.xlsx, .csv) o una foto de una rutina escrita; la app la convierte en borrador para que la revises.',
    ],
  },
  {
    id: 'compartir',
    title: 'Dar una rutina a los socios',
    steps: [
      'Abre la rutina desde Mis rutinas y, en el editor, cambia "Privada" por "Pública".',
      'Los socios la verán en Rutinas → Públicas y podrán pulsar "Guardar copia" para tenerla en sus rutinas.',
      'Dile al socio el nombre exacto de la rutina para que la encuentre.',
    ],
    note:
      'De momento no se puede asignar una rutina a un socio concreto desde la app: el socio tiene que guardarse la copia.',
  },
  {
    id: 'clases',
    title: 'Programar y gestionar clases',
    steps: [
      'Ve a Clases → Mis clases y pulsa "+ Nueva clase".',
      'Rellena nombre, descripción, categoría, hora de inicio, duración, aforo y ubicación.',
      'Marca uno o varios días de la semana: si marcas varios se crea una clase por cada día.',
      'La clase se repite cada semana. Para cambiar algo, pulsa "Editar"; para que deje de salir, "Desactivar".',
      'Los socios reservan desde Clases → Hoy con "Reservar plaza", a partir de las 08:00 del día de la clase. Cuando se llena el aforo entran en lista de espera y suben solos si alguien cancela.',
    ],
  },
  {
    id: 'acceso-socios',
    title: 'Cómo entran los socios (para ayudarles)',
    steps: [
      'Entran en cwsierradegata.com, escriben su email y pulsan "Enviarme un código".',
      'Les llega un código de 8 cifras al correo. Caduca en 15 minutos. Si no lo ven, que miren en spam.',
      'Al meter el código entran. La primera vez se les pide nombre y apellidos, solo una vez.',
      'Si piden otro código, tienen que esperar un minuto entre uno y otro.',
      'Si necesitan algo del centro, lo piden desde Tickets → "Nueva petición"; lo gestiona el administrador.',
    ],
    note: 'Los roles (socio, monitor, administrador) solo los cambia el administrador desde la pestaña Admin.',
  },
];

/** Secciones que solo aplican al administrador; se añaden a las de monitor. */
export const ADMIN_GUIDE_SECTIONS: GuideSection[] = [
  {
    id: 'usuarios-roles',
    title: 'Dar de alta monitores y gestionar socios (solo admin)',
    intro: 'Nadie se registra como monitor: entran como socio y tú les cambias el rol.',
    steps: [
      'Pide al monitor que entre en la app con su email, como un socio más.',
      'Ve a Admin, búscalo en la lista y cambia el selector de "Rol" a Monitor. El cambio es inmediato: le aparecerá la pestaña Monitor la próxima vez que abra la app.',
      'Si un socio se da de baja, pulsa "Eliminar" en su fila: se borra su cuenta y todos sus datos. No se puede deshacer.',
      'Un socio también puede borrarse solo desde el menú de usuario de la app.',
    ],
    note: 'Tu propia fila no se puede editar ni borrar, para que no te quedes sin acceso.',
  },
  {
    id: 'tickets',
    title: 'Atender las peticiones de los socios (solo admin)',
    steps: [
      'Ve a Tickets → Gestionar. Puedes filtrar por estado.',
      'Abre la petición y usa "Cambiar estado a…": En progreso mientras la atiendes, Resuelto cuando esté hecha, Cerrado si no procede.',
      'Puedes añadir notas de resolución; el socio ve el estado en Tickets → Mis tickets.',
    ],
  },
  {
    id: 'clases-admin',
    title: 'Crear clases para otro monitor (solo admin)',
    steps: [
      'En Clases → Mis clases → "+ Nueva clase", el formulario te muestra un campo "Instructor" que los monitores no ven.',
      'Elige al monitor que la va a dar; si lo dejas en "Sin asignar", la clase queda a tu nombre.',
    ],
  },
];
